package com.exura.components.dialogs.imageSelection

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.exura.features.fileStorage.FileStoragePrefixes
import com.exura.features.fileStorage.usecase.ListFilesUseCase
import com.exura.features.fileStorage.usecase.UploadFileUseCase
import com.exura.util.injectAndRemember
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.w3c.files.File
import kotlin.js.Date

sealed interface UserImagesDialogEvent {
    data object LoadImages : UserImagesDialogEvent
    data class SelectImage(val imageUrl: String) : UserImagesDialogEvent
    data class SelectFile(val file: File) : UserImagesDialogEvent
    data class ConfirmSelection(
        val onSuccess: (String?) -> Unit,
        val onDismiss: () -> Unit
    ) : UserImagesDialogEvent
}

data class UserImagesDialogUiState(
    val isLoading: Boolean = true,
    val isUploading: Boolean = false,
    val error: String? = null,
    val existingImages: List<String> = emptyList(),
    val selectedImageUrl: String? = null,
    val pendingFile: File? = null,
    val initialImageUrl: String? = null
)

class UserImagesDialogController(
    private val initialImageUrl: String?,
    private val listFilesUseCase: ListFilesUseCase,
    private val uploadFileUseCase: UploadFileUseCase,
) {
    var uiState by mutableStateOf(UserImagesDialogUiState(
        selectedImageUrl = initialImageUrl,
        initialImageUrl = initialImageUrl
    ))
        private set

    suspend fun loadImages() {
            uiState = uiState.copy(isLoading = true, error = null)
            listFilesUseCase(FileStoragePrefixes.PLAN_DEFINITION_IMAGES)
                .onSuccess { files ->
                    val imageUrls = files.map { it.getDownloadUrl() }
                    uiState = uiState.copy(
                        isLoading = false,
                        existingImages = imageUrls
                    )
                }
                .onFailure { error ->
                    uiState = uiState.copy(
                        isLoading = false,
                        error = error.message
                    )
                }

    }

    fun onSelectImage(imageUrl: String) {
        uiState = uiState.copy(
            selectedImageUrl = imageUrl,
            pendingFile = null
        )
    }

    suspend fun onSelectFile(file: File) {
        try {
            val processedFile = ImageProcessor.processImage(file)
            val objectUrl = org.w3c.dom.url.URL.createObjectURL(processedFile)
            uiState = uiState.copy(
                pendingFile = processedFile,
                selectedImageUrl = objectUrl
            )
        } catch (e: Exception) {
            uiState = uiState.copy(error = e.message)
        }
    }

    suspend fun onConfirmSelection(onSuccess: (String?) -> Unit, onDismiss: () -> Unit) {
        val pendingFile = uiState.pendingFile
        
        if (pendingFile != null) {
            uiState = uiState.copy(isUploading = true)
            val fileName = "${Date.now().toLong()}_${pendingFile.name}"
            uploadFileUseCase(
                FileStoragePrefixes.PLAN_DEFINITION_IMAGES,
                fileName,
                pendingFile,
            )
                .onSuccess { fileRef ->
                    onSuccess(fileRef.getDownloadUrl())
                    uiState = uiState.copy(isUploading = false)
                    onDismiss()
                }
                .onFailure { error ->
                    uiState = uiState.copy(error = error.message, isUploading = false)
                }
        } else {
            onSuccess(uiState.selectedImageUrl)
            onDismiss()
        }
    }

    suspend fun handleEvent(event: UserImagesDialogEvent) {
        when (event) {
            is UserImagesDialogEvent.LoadImages -> loadImages()
            is UserImagesDialogEvent.SelectImage -> onSelectImage(event.imageUrl)
            is UserImagesDialogEvent.SelectFile -> onSelectFile(event.file)
            is UserImagesDialogEvent.ConfirmSelection -> onConfirmSelection(event.onSuccess, event.onDismiss)
        }
    }
}

@Composable
fun rememberUserImagesDialogController(
    initialImageUrl: String?,
): UserImagesDialogController {
    val listFilesUseCase = injectAndRemember<ListFilesUseCase>()
    val uploadFileUseCase = injectAndRemember<UploadFileUseCase>()

    return remember(initialImageUrl) {
        UserImagesDialogController(
            initialImageUrl = initialImageUrl,
            listFilesUseCase = listFilesUseCase,
            uploadFileUseCase = uploadFileUseCase,
        )
    }
}
